import { ChangeEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import "./App.css";
import brandIcon from "./assets/brand/icon-no-bg.png";
import instituteLogo from "./assets/icons/ivmr.svg";
import screenshotDaily from "./assets/screenshots/01-daily-practice.png";
import screenshotFlows from "./assets/screenshots/02-practice-flows.png";
import screenshotGuided from "./assets/screenshots/03-guided-techniques.png";
import screenshotCue from "./assets/screenshots/04-customize-cues.png";
import screenshotShare from "./assets/screenshots/05-share-practices.png";
import screenshotWidget from "./assets/screenshots/08-home-screen-widget.png";
import freedivingApneaSession from "./assets/freediving/01-apnea-session.png";
import freedivingDryPractice from "./assets/freediving/02-dry-breath-hold.png";
import freedivingTimingCues from "./assets/freediving/03-timing-cues.png";
import freedivingWidgets from "./assets/freediving/04-widgets-reminders.png";
import freedivingSaveRoutines from "./assets/freediving/05-save-routines.png";
import freedivingPreciseControl from "./assets/freediving/06-precise-control.png";
import { getCanonicalLocale } from "./i18n";
import { localeOptions, type SupportedLocale } from "./i18n/generated";
import {
  APP_STORE_BADGE,
  GOOGLE_PLAY_BADGES,
  RUSTORE_BADGE,
  STORE_LINKS,
} from "./storeBadges";
import SupportPage from "./SupportPage";

type ThemeName = "light" | "dark";
type ThemePreference = ThemeName | "system";

const FEATURE_LIMIT = 4;
const THEME_STORAGE_KEY = "vrittiLandingTheme";
const THEME_OPTIONS: ThemePreference[] = ["system", "light", "dark"];
const INSTITUTE_URL = "https://yogatherapia.ru/";
const MAIN_PAGE_URL = "/";
const PRIVACY_POLICY_URL = "/privacy-policy.html";
const RUSTORE_BADGE_ALT = "Скачать в RuStore";
const FREEDIVING_PAGE_PATH = "/freediving";
const SUPPORT_PAGE_PATH = "/support";

const screenshotAssets = [
  screenshotDaily,
  screenshotFlows,
  screenshotGuided,
  screenshotCue,
  screenshotShare,
  screenshotWidget,
];

const freedivingScreenshotAssets = [
  freedivingApneaSession,
  freedivingDryPractice,
  freedivingTimingCues,
  freedivingWidgets,
  freedivingSaveRoutines,
  freedivingPreciseControl,
];

function getSystemTheme(): ThemeName {
  if (
    typeof window === "undefined" ||
    typeof window.matchMedia !== "function"
  ) {
    return "light";
  }

  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

function getStoredThemePreference(): ThemePreference {
  if (typeof window === "undefined") {
    return "system";
  }

  const storedTheme = window.localStorage.getItem(THEME_STORAGE_KEY);

  if (
    storedTheme === "light" ||
    storedTheme === "dark" ||
    storedTheme === "system"
  ) {
    return storedTheme;
  }

  return "system";
}

function updateMetaTag(selector: string, content: string) {
  const element = document.querySelector<HTMLMetaElement>(selector);
  if (element) {
    element.setAttribute("content", content);
  }
}

interface StoreBadgeProps {
  href: string;
  imageSrc: string;
  alt: string;
}

function StoreBadge({ href, imageSrc, alt }: StoreBadgeProps) {
  return (
    <a className="store-badge" href={href} target="_blank" rel="noreferrer">
      <img src={imageSrc} alt={alt} />
    </a>
  );
}

interface FreedivingButtonProps {
  href: string;
  children: string;
  variant?: "primary" | "secondary";
  external?: boolean;
}

function FreedivingButton({
  href,
  children,
  variant = "primary",
  external = true,
}: FreedivingButtonProps) {
  return (
    <a
      className={`freediving-button freediving-button--${variant}`}
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noreferrer" : undefined}
    >
      {children}
    </a>
  );
}

interface FreedivingLandingProps {
  googlePlayBadge: string;
  handleLanguageChange: (event: ChangeEvent<HTMLSelectElement>) => void;
  locale: SupportedLocale;
  localeOption: (typeof localeOptions)[number];
  showRuStoreBadge: boolean;
}

function isFreedivingPath() {
  if (typeof window === "undefined") {
    return false;
  }

  return (
    window.location.pathname.replace(/\/+$/, "") === FREEDIVING_PAGE_PATH
  );
}

function isSupportPath() {
  if (typeof window === "undefined") {
    return false;
  }

  return window.location.pathname.replace(/\/+$/, "") === SUPPORT_PAGE_PATH;
}

function FreedivingLanding({
  googlePlayBadge,
  handleLanguageChange,
  locale,
  localeOption,
  showRuStoreBadge,
}: FreedivingLandingProps) {
  const { t } = useTranslation();
  const benefits = t("freediving.benefits", {
    returnObjects: true,
  }) as string[];
  const screenshotCaptions = t("freediving.screenshotCaptions", {
    returnObjects: true,
  }) as string[];
  const useCases = t("freediving.useCases", {
    returnObjects: true,
  }) as string[];
  const trustPoints = t("freediving.trustPoints", {
    returnObjects: true,
  }) as string[];

  return (
    <div className="freediving-page" dir={localeOption.dir}>
      <header className="freediving-nav">
        <a className="freediving-brand" href={MAIN_PAGE_URL}>
          <img className="freediving-brand__icon" src={brandIcon} alt="" />
          <span>Vritti</span>
        </a>

        <nav
          className="freediving-nav__links"
          aria-label={t("freediving.navLabel")}
        >
          <a href="#why">{t("freediving.navWhy")}</a>
          <a href="#screenshots">{t("freediving.navScreenshots")}</a>
          <a href={PRIVACY_POLICY_URL}>{t("freediving.navPrivacy")}</a>
          <FreedivingButton
            href={MAIN_PAGE_URL}
            variant="secondary"
            external={false}
          >
            {t("freediving.mainPageCta")}
          </FreedivingButton>
        </nav>

        <label className="language-select freediving-language-select">
          <span className="language-select__label">
            {t("landing.languageLabel")}
          </span>
          <select value={locale} onChange={handleLanguageChange}>
            {localeOptions.map((option) => (
              <option key={option.code} value={option.code}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      </header>

      <main>
        <section className="freediving-hero">
          <div className="freediving-hero__copy">
            <p className="freediving-kicker">{t("freediving.heroKicker")}</p>
            <h1>{t("freediving.heroTitle")}</h1>
            <p className="freediving-hero__body">
              {t("freediving.heroText")}
            </p>

            <div className="store-badges freediving-store-badges">
              <StoreBadge
                href={STORE_LINKS.freedivingAppStore}
                imageSrc={APP_STORE_BADGE}
                alt={t("landing.appStoreBadgeAlt")}
              />
              <StoreBadge
                href={STORE_LINKS.googlePlay}
                imageSrc={googlePlayBadge}
                alt={t("landing.googlePlayBadgeAlt")}
              />
              {showRuStoreBadge && (
                <StoreBadge
                  href={STORE_LINKS.ruStore}
                  imageSrc={RUSTORE_BADGE}
                  alt={RUSTORE_BADGE_ALT}
                />
              )}
            </div>
            <p className="freediving-note">{t("freediving.heroNote")}</p>
          </div>

          <div
            className="freediving-hero__visual hero__visual"
            aria-hidden="true"
          >
            <div className="hero__halo" />
            <figure className="device device--primary">
              <img src={freedivingDryPractice} alt="" />
            </figure>
            <figure className="device device--secondary">
              <img src={freedivingApneaSession} alt="" />
            </figure>
            <figure className="device device--tertiary">
              <img src={freedivingTimingCues} alt="" />
            </figure>
            <div className="hero__seal">
              <img src={brandIcon} alt="" />
            </div>
          </div>
        </section>

        <section className="freediving-section" id="why">
          <div className="freediving-section__intro">
            <p className="freediving-kicker">{t("freediving.whyKicker")}</p>
            <h2>{t("freediving.whyTitle")}</h2>
            <p>{t("freediving.whyText")}</p>
          </div>

          <ul className="freediving-benefits">
            {benefits.map((benefit) => (
              <li key={benefit}>{benefit}</li>
            ))}
          </ul>
        </section>

        <section className="freediving-section" id="screenshots">
          <div className="freediving-section__intro">
            <p className="freediving-kicker">
              {t("freediving.featuresKicker")}
            </p>
            <h2>{t("freediving.featuresTitle")}</h2>
          </div>

          <div className="freediving-screenshot-grid">
            {freedivingScreenshotAssets.map((screenshot, index) => (
              <figure
                className="freediving-shot"
                key={screenshotCaptions[index] ?? screenshot}
              >
                <img
                  src={screenshot}
                  alt={screenshotCaptions[index] ?? ""}
                  loading={index < 2 ? "eager" : "lazy"}
                />
                <figcaption>{screenshotCaptions[index]}</figcaption>
              </figure>
            ))}
          </div>
        </section>

        <section className="freediving-section freediving-section--split">
          <article className="freediving-explainer">
            <h2>{t("freediving.designedTitle")}</h2>
            <p>{t("freediving.designedText")}</p>
          </article>

          <article className="freediving-explainer freediving-explainer--warning">
            <h2>{t("freediving.notTitle")}</h2>
            <p>{t("freediving.notText")}</p>
          </article>
        </section>

        <section className="freediving-section">
          <div className="freediving-section__intro">
            <p className="freediving-kicker">{t("freediving.useKicker")}</p>
            <h2>{t("freediving.useTitle")}</h2>
          </div>

          <div className="freediving-card-grid">
            {useCases.map((useCase) => (
              <article className="freediving-card" key={useCase}>
                {useCase}
              </article>
            ))}
          </div>
        </section>

        <section className="freediving-section freediving-trust">
          <div className="freediving-section__intro">
            <p className="freediving-kicker">{t("freediving.trustKicker")}</p>
            <h2>{t("freediving.trustTitle")}</h2>
          </div>

          <ul className="freediving-trust__list">
            {trustPoints.map((point) => (
              <li key={point}>{point}</li>
            ))}
          </ul>

          <a className="freediving-inline-link" href={PRIVACY_POLICY_URL}>
            {t("freediving.privacyLink")}
          </a>
        </section>

        <section className="freediving-final-cta">
          <p className="freediving-kicker">{t("freediving.finalKicker")}</p>
          <h2>{t("freediving.finalTitle")}</h2>
          <p>{t("freediving.finalText")}</p>

          <div className="store-badges store-badges--centered freediving-store-badges">
            <StoreBadge
              href={STORE_LINKS.freedivingAppStore}
              imageSrc={APP_STORE_BADGE}
              alt={t("landing.appStoreBadgeAlt")}
            />
            <StoreBadge
              href={STORE_LINKS.googlePlay}
              imageSrc={googlePlayBadge}
              alt={t("landing.googlePlayBadgeAlt")}
            />
            {showRuStoreBadge && (
              <StoreBadge
                href={STORE_LINKS.ruStore}
                imageSrc={RUSTORE_BADGE}
                alt={RUSTORE_BADGE_ALT}
              />
            )}
          </div>

          <p className="freediving-safety-note">{t("freediving.safetyNote")}</p>
        </section>
      </main>
    </div>
  );
}

function App() {
  const { i18n, t } = useTranslation();
  const isFreedivingPage = isFreedivingPath();
  const isSupportPage = isSupportPath();
  const [systemTheme, setSystemTheme] = useState<ThemeName>(() =>
    getSystemTheme(),
  );
  const [themePreference, setThemePreference] = useState<ThemePreference>(() =>
    getStoredThemePreference(),
  );
  const locale = getCanonicalLocale(i18n.resolvedLanguage);
  const localeOption =
    localeOptions.find((option) => option.code === locale) ?? localeOptions[0];
  const googlePlayBadge = GOOGLE_PLAY_BADGES[locale];
  const showRuStoreBadge = locale === "ru";
  const theme = themePreference === "system" ? systemTheme : themePreference;
  const featureList = (
    t("content.features", { returnObjects: true }) as string[]
  ).slice(0, FEATURE_LIMIT);
  const footerNoteBefore = t("landing.footerNoteBefore");
  const footerNoteAfter = t("landing.footerNoteAfter");
  const instituteName = t("landing.instituteName");
  const themeLabels: Record<ThemePreference, string> = {
    system: t("landing.themeSystem"),
    light: t("landing.themeLight"),
    dark: t("landing.themeDark"),
  };

  useEffect(() => {
    if (typeof window.matchMedia !== "function") {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = (event: MediaQueryListEvent) => {
      setSystemTheme(event.matches ? "dark" : "light");
    };

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    window.localStorage.setItem(THEME_STORAGE_KEY, themePreference);
  }, [themePreference]);

  useEffect(() => {
    const pageTitle = `${t("meta.appName")} | ${t("meta.badge")}`;
    const description = t("content.promoText");

    if (isFreedivingPage) {
      const freedivingTitle = t("freediving.metaTitle");
      const freedivingDescription = t("freediving.metaDescription");

      document.documentElement.lang = locale;
      document.documentElement.dir = localeOption.dir;
      document.body.style.backgroundColor = "#050708";
      document.title = freedivingTitle;

      updateMetaTag('meta[name="description"]', freedivingDescription);
      updateMetaTag('meta[property="og:title"]', freedivingTitle);
      updateMetaTag(
        'meta[property="og:description"]',
        freedivingDescription,
      );
      updateMetaTag('meta[name="twitter:title"]', freedivingTitle);
      updateMetaTag(
        'meta[name="twitter:description"]',
        freedivingDescription,
      );
      updateMetaTag(
        'meta[name="theme-color"][data-dynamic="true"]',
        "#050708",
      );
      return;
    }

    if (isSupportPage) {
      const supportTitle = t("support.metaTitle");
      const supportDescription = t("support.metaDescription");

      document.documentElement.lang = locale;
      document.documentElement.dir = localeOption.dir;
      document.body.style.backgroundColor =
        theme === "dark" ? "#000000" : "#ffffff";
      document.title = supportTitle;

      updateMetaTag('meta[name="description"]', supportDescription);
      updateMetaTag('meta[property="og:title"]', supportTitle);
      updateMetaTag('meta[property="og:description"]', supportDescription);
      updateMetaTag('meta[name="twitter:title"]', supportTitle);
      updateMetaTag('meta[name="twitter:description"]', supportDescription);
      updateMetaTag(
        'meta[name="theme-color"][data-dynamic="true"]',
        theme === "dark" ? "#000000" : "#ffffff",
      );
      return;
    }

    document.documentElement.lang = locale;
    document.documentElement.dir = localeOption.dir;
    document.body.style.backgroundColor =
      theme === "dark" ? "#000000" : "#ffffff";
    document.title = pageTitle;

    updateMetaTag('meta[name="description"]', description);
    updateMetaTag('meta[property="og:title"]', pageTitle);
    updateMetaTag('meta[property="og:description"]', description);
    updateMetaTag('meta[name="twitter:title"]', pageTitle);
    updateMetaTag('meta[name="twitter:description"]', description);
    updateMetaTag(
      'meta[name="theme-color"][data-dynamic="true"]',
      theme === "dark" ? "#000000" : "#ffffff",
    );
  }, [isFreedivingPage, isSupportPage, locale, localeOption.dir, t, theme]);

  const handleLanguageChange = (event: ChangeEvent<HTMLSelectElement>) => {
    void i18n.changeLanguage(event.target.value as SupportedLocale);
  };

  const handleThemeChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setThemePreference(event.target.value as ThemePreference);
  };

  if (isFreedivingPage) {
    return (
      <FreedivingLanding
        googlePlayBadge={googlePlayBadge}
        handleLanguageChange={handleLanguageChange}
        locale={locale}
        localeOption={localeOption}
        showRuStoreBadge={showRuStoreBadge}
      />
    );
  }

  if (isSupportPage) {
    return (
      <SupportPage
        handleLanguageChange={handleLanguageChange}
        handleThemeChange={handleThemeChange}
        locale={locale}
        localeOption={localeOption}
        theme={theme}
        themeLabels={themeLabels}
        themePreference={themePreference}
      />
    );
  }

  return (
    <div className="page" data-theme={theme} dir={localeOption.dir}>
      <div className="page__ambient page__ambient--top" />
      <div className="page__ambient page__ambient--bottom" />

      <header className="topbar">
        <a className="brand" href="#top">
          <img className="brand__icon" src={brandIcon} alt="" />
          <div>
            <span className="brand__name">{t("meta.appName")}</span>
            <span className="brand__badge">{t("meta.badge")}</span>
          </div>
        </a>

        <div className="topbar__controls">
          <label className="language-select">
            <span className="language-select__label">
              {t("landing.languageLabel")}
            </span>
            <select value={locale} onChange={handleLanguageChange}>
              {localeOptions.map((option) => (
                <option key={option.code} value={option.code}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="theme-picker">
            <span className="theme-picker__label">
              {t("landing.themeLabel")}
            </span>
            <select value={themePreference} onChange={handleThemeChange}>
              {THEME_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {themeLabels[option]}
                </option>
              ))}
            </select>
          </label>
        </div>
      </header>

      <main>
        <section className="hero" id="top">
          <div className="hero__copy">
            <span className="hero__badge">{t("meta.badge")}</span>
            <h1>{t("landing.heroTitle")}</h1>
            <p className="hero__body">{t("content.promoText")}</p>

            <div className="store-badges">
              <StoreBadge
                href={STORE_LINKS.appStore}
                imageSrc={APP_STORE_BADGE}
                alt={t("landing.appStoreBadgeAlt")}
              />
              <StoreBadge
                href={STORE_LINKS.googlePlay}
                imageSrc={googlePlayBadge}
                alt={t("landing.googlePlayBadgeAlt")}
              />
              {showRuStoreBadge && (
                <StoreBadge
                  href={STORE_LINKS.ruStore}
                  imageSrc={RUSTORE_BADGE}
                  alt={RUSTORE_BADGE_ALT}
                />
              )}
            </div>
          </div>

          <div className="hero__visual" aria-hidden="true">
            <div className="hero__halo" />
            <figure className="device device--primary">
              <img src={screenshotDaily} alt="" />
            </figure>
            <figure className="device device--secondary">
              <img src={screenshotFlows} alt="" />
            </figure>
            <figure className="device device--tertiary">
              <img src={screenshotCue} alt="" />
            </figure>
            <div className="hero__seal">
              <img src={brandIcon} alt="" />
            </div>
          </div>
        </section>

        <section className="section">
          <div className="section__intro">
            <h2>{t("landing.featuresTitle")}</h2>
            <p>{t("content.summary")}</p>
            <p>{t("content.details")}</p>
          </div>

          <div className="feature-grid">
            {featureList.map((feature, index) => (
              <article
                className="feature-card"
                key={feature}
                style={{ animationDelay: `${index * 90}ms` }}
              >
                <span className="feature-card__number">
                  {(index + 1).toString().padStart(2, "0")}
                </span>
                <p>{feature}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="section">
          <div className="section__intro">
            <h2>{t("landing.galleryTitle")}</h2>
          </div>

          <div className="gallery">
            {screenshotAssets.map((screenshot, index) => (
              <figure className="gallery-card" key={screenshot}>
                <img
                  src={screenshot}
                  alt=""
                  loading={index < 2 ? "eager" : "lazy"}
                />
              </figure>
            ))}
          </div>
        </section>

        <section className="section section--closing" id="download">
          <div className="closing-card">
            <span className="closing-card__badge">{t("meta.badge")}</span>
            <h2>{t("landing.closingTitle")}</h2>
            <p>{t("content.closing")}</p>

            <div className="store-badges store-badges--centered">
              <StoreBadge
                href={STORE_LINKS.appStore}
                imageSrc={APP_STORE_BADGE}
                alt={t("landing.appStoreBadgeAlt")}
              />
              <StoreBadge
                href={STORE_LINKS.googlePlay}
                imageSrc={googlePlayBadge}
                alt={t("landing.googlePlayBadgeAlt")}
              />
              {showRuStoreBadge && (
                <StoreBadge
                  href={STORE_LINKS.ruStore}
                  imageSrc={RUSTORE_BADGE}
                  alt={RUSTORE_BADGE_ALT}
                />
              )}
            </div>

            <div className="closing-card__support">
              <img
                className="closing-card__logo"
                src={instituteLogo}
                alt=""
                aria-hidden="true"
              />
              <p id="closing-card-note" className="closing-card__note">
                {footerNoteBefore}
                <a
                  className="closing-card__link"
                  href={INSTITUTE_URL}
                  target="_blank"
                  rel="noreferrer"
                >
                  {instituteName}
                </a>
                {footerNoteAfter}
              </p>
            </div>
          </div>
        </section>
      </main>

      <div className="mobile-store-bar">
        <div className="mobile-store-bar__copy">
          <span className="mobile-store-bar__name">{t("meta.appName")}</span>
          <span className="mobile-store-bar__badge">{t("meta.badge")}</span>
        </div>

        <div className="mobile-store-bar__actions">
          <StoreBadge
            href={STORE_LINKS.appStore}
            imageSrc={APP_STORE_BADGE}
            alt={t("landing.appStoreBadgeAlt")}
          />
          <StoreBadge
            href={STORE_LINKS.googlePlay}
            imageSrc={googlePlayBadge}
            alt={t("landing.googlePlayBadgeAlt")}
          />
          {showRuStoreBadge && (
            <StoreBadge
              href={STORE_LINKS.ruStore}
              imageSrc={RUSTORE_BADGE}
              alt={RUSTORE_BADGE_ALT}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default App;
